import { defineStore } from "pinia";
import { dataService } from "@thegetty-private/rad-app-utils";

import {
  getRootLocation,
  getGalleryLocation,
  getFloorName,
  getRoomName
} from "@/helpers/Place";
import { getTechnicalMetadataFields } from "@/helpers/LinkedArt";
import { transformSearchResultToThumbnailItemWithArtist } from "@/helpers/SearchItemHelper";
import * as configHelper from "@/helpers/configHelper";

import {
  getLocationMessage,
  getObjectDescription
} from "@thegetty-private/linked-data.js";

const includePartsInQueries = configHelper.includePartsInQueries();
let apiPath = configHelper.getApiPath("/");
let baseUrl = configHelper.getDataUrl("/");

/*
 * allow the overwriting of the app url (testing)
 *
 * @param {string} url
 */
export function setApiPath(url) {
  apiPath = url;
}

/**
 * allow the overwriting of the data url (testing)
 *
 * @param {string} url
 */
export function setDataUrl(url) {
  baseUrl = url;
}

export const useGalleryStore = defineStore("gallery", {
  state: () => ({ items: {} }),
  actions: {
    async addGallery(indexedId, translate) {
      if (this.items[indexedId]) {
        return;
      }

      // initial fetch
      let lodObject;
      try {
        lodObject = await dataService.getAllById(indexedId, baseUrl);
        this.items[indexedId] = { id: indexedId };
      } catch (err) {
        console.error(err);
        return;
      }

      const ref = this.items[indexedId];

      // Gallery location
      try {
        const rootLocation = getRootLocation(lodObject);
        if (rootLocation) {
          const placeRoot = await dataService.getAllById(rootLocation, baseUrl);
          ref.galleryLocation = getGalleryLocation(placeRoot);
        }
      } catch (err) {
        console.error(err);
      }

      ref.pageTitle = getLocationMessage(lodObject);
      ref.description = getObjectDescription(lodObject, "text/markdown");
      ref.floor = getFloorName(lodObject);
      ref.room = getRoomName(lodObject);

      // Technical metadata
      ref.technicalMetadata = getTechnicalMetadataFields(lodObject, translate);

      // Related search
      try {
        const url = apiPath + "api/search";
        let searchParams = {
          current_location_id: [indexedId],
          sort: "title_sort",
          size: 500
        };

        if (!includePartsInQueries) {
          searchParams.is_standalone = true;
        }

        const response = await dataService.axiosGet(
          url,
          undefined,
          searchParams
        );

        if (response && response.data) {
          const items = response.data.data.map((item) => {
            let rec = transformSearchResultToThumbnailItemWithArtist(
              item,
              false,
              translate
            );
            // hack for the current world where the RelatedSearchResults component can transform records too
            rec.isTransformed = true;
            return rec;
          });

          ref.onView = {
            items: items,
            params: searchParams,
            total: response.data.total
          };
        }
      } catch (err) {
        console.error(err);
      }

      // Current Exhibitions
      try {
        const url = apiPath + "api/currentExhibitions";
        const response = await dataService.axiosGet(url, undefined, {
          id: indexedId
        });
        ref.currentExhibitions = response ? response.data : [];
      } catch (err) {
        console.error(err);
      }
    }
  },
  getters: {
    getGallery(state) {
      return (indexedId) => state.items[indexedId];
    }
  }
});
